import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { UserPlus, Settings2, ChevronRight, Download, Loader2 } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useFiscalYear } from "@/context/FiscalYearContext";
import { PageHeader, Loading, EmptyState, StatCard, OfficeSelect, groupByOffice, OfficeSectionHeader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export const ONB_STATUS_CLS = {
  対象外: "bg-slate-100 text-slate-600", 未開始: "bg-slate-100 text-slate-600",
  受講中: "bg-blue-100 text-blue-700", 研修修了: "bg-indigo-100 text-indigo-700",
  オリエンテーション未完了: "bg-amber-100 text-amber-700", 管理者確認待ち: "bg-amber-100 text-amber-800",
  修了: "bg-green-100 text-green-700", 期限超過: "bg-rose-100 text-rose-700",
};

export function OnbBadge({ status }) {
  return <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${ONB_STATUS_CLS[status] || "bg-slate-100 text-slate-600"}`} data-testid="onb-status">{status}</span>;
}

export default function AdminOnboarding() {
  const { user } = useAuth();
  const { fiscalYear } = useFiscalYear();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [dash, setDash] = useState({});
  const [assignOpen, setAssignOpen] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [offices, setOffices] = useState([]);
  const [officeId, setOfficeId] = useState("");

  const load = useCallback(() => {
    const oq = officeId ? `?office_id=${officeId}` : "";
    api.get(`/onboarding/employees${oq}`).then(({ data }) => setRows(data));
    if (fiscalYear) api.get(`/onboarding/dashboard?fiscal_year=${fiscalYear}${officeId ? `&office_id=${officeId}` : ""}`).then(({ data }) => setDash(data));
  }, [fiscalYear, officeId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/offices").then(({ data }) => setOffices(data)); }, []);

  const openAssign = async () => {
    const { data } = await api.get("/users");
    const targetIds = new Set((rows || []).map((r) => r.user_id));
    setStaffList(data.filter((u) => u.role === "staff" && !targetIds.has(u.id)));
    setPicked([]); setAssignOpen(true);
  };

  const doAssign = async () => {
    if (!picked.length) { toast.error("職員を選択してください"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/onboarding/employees/bulk-assign", { user_ids: picked });
      toast.success(`${data.assigned}名に入社研修を割り当てました`);
      setAssignOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const exportCsv = () => {
    const head = ["氏名", "事業所", "職種", "雇用形態", "入職日", "対象研修数", "修了数", "進捗率", "受講期限", "状態", "修了日"];
    const lines = [head.join(",")].concat((rows || []).map((r) => [
      r.name, r.office_name, r.job_type || "", r.employment_type || "", r.hire_date || "",
      r.total_items || 0, r.done_items || 0, `${r.progress || 0}%`, r.due_date || "", r.status, r.completed_at || "",
    ].map((x) => `"${String(x).replace(/"/g, '""')}"`).join(",")));
    const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "onboarding_progress.csv"; a.click();
  };

  if (!rows) return <Loading />;

  const onbHeader = (
    <TableRow>
      <TableHead>氏名</TableHead><TableHead>事業所</TableHead><TableHead>職種</TableHead>
      <TableHead>入職日</TableHead><TableHead className="text-center">研修</TableHead>
      <TableHead className="text-center">進捗</TableHead><TableHead>受講期限</TableHead>
      <TableHead>状態</TableHead><TableHead></TableHead>
    </TableRow>
  );
  const renderOnbRows = (list) => list.map((r) => (
    <TableRow key={r.user_id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/onboarding/${r.user_id}`)} data-testid={`onb-row-${r.user_id}`}>
      <TableCell className="font-medium text-slate-800">{r.name}</TableCell>
      <TableCell className="text-sm text-slate-500">{r.office_name}</TableCell>
      <TableCell className="text-sm text-slate-500">{r.job_type || "-"}</TableCell>
      <TableCell className="text-sm text-slate-500">{r.hire_date || "-"}</TableCell>
      <TableCell className="text-center text-sm">{r.done_items ?? 0}/{r.total_items ?? 0}</TableCell>
      <TableCell className="text-center">
        <div className="flex items-center gap-2">
          <div className="w-16 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-blue-500" style={{ width: `${r.progress || 0}%` }} /></div>
          <span className="text-xs text-slate-500">{r.progress || 0}%</span>
        </div>
      </TableCell>
      <TableCell className="text-sm text-slate-500">{r.due_date || "-"}</TableCell>
      <TableCell><OnbBadge status={r.status} /></TableCell>
      <TableCell><ChevronRight className="w-4 h-4 text-slate-300" /></TableCell>
    </TableRow>
  ));
  const onbSections = officeId ? [] : groupByOffice(rows, offices, (r) => [r.office_id]);

  return (
    <div>
      <PageHeader
        title="入社研修" subtitle="新規雇用職員の入社研修の進捗・修了状況を管理します"
        actions={
          <div className="flex gap-2 items-center">
            <OfficeSelect offices={offices} value={officeId} onChange={setOfficeId} testid="onb-office-filter" />
            <Button variant="outline" onClick={exportCsv} data-testid="onb-export-csv"><Download className="w-4 h-4 mr-1" />進捗CSV</Button>
            <Button variant="outline" onClick={openAssign} data-testid="onb-assign-existing"><UserPlus className="w-4 h-4 mr-1" />既存職員に割当</Button>
            {user.role === "corp_admin" && (
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => navigate("/onboarding-settings")} data-testid="onb-settings-btn"><Settings2 className="w-4 h-4 mr-1" />入社研修設定</Button>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3 mb-6">
        <StatCard testid="onb-kpi-hires" label="今年度入職" value={dash.this_year_hires ?? 0} unit="名" icon={UserPlus} tone="blue" />
        <StatCard testid="onb-kpi-targets" label="対象者" value={dash.targets ?? 0} unit="名" icon={UserPlus} tone="slate" />
        <StatCard testid="onb-kpi-notstarted" label="未開始" value={dash.not_started ?? 0} unit="名" icon={UserPlus} tone="slate" />
        <StatCard testid="onb-kpi-inprogress" label="受講中" value={dash.in_progress ?? 0} unit="名" icon={UserPlus} tone="blue" />
        <StatCard testid="onb-kpi-pending" label="管理者確認待ち" value={dash.pending_confirm ?? 0} unit="名" icon={UserPlus} tone="amber" />
        <StatCard testid="onb-kpi-completed" label="修了" value={dash.completed ?? 0} unit="名" icon={UserPlus} tone="green" />
        <StatCard testid="onb-kpi-overdue" label="期限超過" value={dash.overdue ?? 0} unit="名" icon={UserPlus} tone="rose" />
      </div>

      {rows.length ? (
        officeId ? (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <Table><TableHeader>{onbHeader}</TableHeader><TableBody>{renderOnbRows(rows)}</TableBody></Table>
          </div>
        ) : (
          <div className="space-y-4">
            {onbSections.map((sec) => (
              <div key={sec.office.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <OfficeSectionHeader name={sec.office.name} count={sec.items.length} />
                <Table><TableHeader>{onbHeader}</TableHeader><TableBody>{renderOnbRows(sec.items)}</TableBody></Table>
              </div>
            ))}
          </div>
        )
      ) : <EmptyState title="入社研修の対象者がいません" hint="「既存職員に割当」または職員登録時に対象へ設定してください" />}

      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>既存職員に入社研修を割り当て</DialogTitle></DialogHeader>
          <div className="space-y-1 max-h-72 overflow-y-auto border rounded-lg p-2">
            {staffList.length ? staffList.map((u) => (
              <label key={u.id} className="flex items-center gap-2 text-sm py-1.5 cursor-pointer" data-testid={`onb-pick-${u.id}`}>
                <Checkbox checked={picked.includes(u.id)} onCheckedChange={(c) => setPicked(c ? [...picked, u.id] : picked.filter((x) => x !== u.id))} />
                <span className="font-medium text-slate-700">{u.name}</span>
                <span className="text-xs text-slate-400">{u.office_names?.join("/") || ""}</span>
              </label>
            )) : <p className="text-sm text-slate-400 p-3 text-center">割当可能な職員がいません</p>}
          </div>
          <DialogFooter><Button onClick={doAssign} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="onb-assign-confirm">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `${picked.length}名に割り当て`}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
